import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Search, 
  Plus, 
  Minus, 
  Trash2, 
  Send, 
  ShoppingCart,
  Package,
  X,
  RefreshCw
} from 'lucide-react';
import Modal from '../components/Modal';
import QuickOrderInput from '../components/QuickOrderInput';
import { useAuth } from '../contexts/AuthContext';

function NuovoOrdine() {
  const { user } = useAuth();
  const [fornitori, setFornitori] = useState([]);
  const [ordineProdotti, setOrdineProdotti] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [editingOrdine, setEditingOrdine] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [notePersonalizzate, setNotePersonalizzate] = useState({});
  const [dataOrdine, setDataOrdine] = useState(new Date().toISOString().split('T')[0]);
  const [showOrdineEsistenteModal, setShowOrdineEsistenteModal] = useState(false);
  const [ordineEsistenteId, setOrdineEsistenteId] = useState(null);
  const [showWhatsAppLinksModal, setShowWhatsAppLinksModal] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [ordineIdWhatsApp, setOrdineIdWhatsApp] = useState(null);
  const [showDeleteProdottoModal, setShowDeleteProdottoModal] = useState(false);
  const [prodottoToDelete, setProdottoToDelete] = useState(null);
  const [suggerimentiAI, setSuggerimentiAI] = useState([]);
  const [loadingSuggerimenti, setLoadingSuggerimenti] = useState(false);
  const [showSuggerimenti, setShowSuggerimenti] = useState(true);
  const [showTipologiaModal, setShowTipologiaModal] = useState(false);
  const [prodottoPendingTipologia, setProdottoPendingTipologia] = useState(null);
  const [dataOrdineCollapsed, setDataOrdineCollapsed] = useState(false);
  const [ultimoProdottoInserito, setUltimoProdottoInserito] = useState(null);
  const [focusSearchAfterAdd, setFocusSearchAfterAdd] = useState(false);
  const searchRef = useRef(null);
  const autoSaveDebounceRef = useRef(null);
  const hasCreatedDraftRef = useRef(false);
  const saveOrdineInBackgroundRef = useRef(null);
  const navigate = useNavigate();

  // Riporta il focus sul campo "Cerca prodotti" dopo l'aggiunta di un prodotto (frame successivo, dopo toast/layout)
  useEffect(() => {
    if (focusSearchAfterAdd && searchRef.current) {
      const input = searchRef.current;
      setFocusSearchAfterAdd(false);
      requestAnimationFrame(() => input.focus());
    }
  }, [focusSearchAfterAdd]);

  useEffect(() => {
    loadData();
    
    // Controlla se c'è un ordine da riaprire
    const ordineDaRiaprire = localStorage.getItem('ordineDaRiaprire');
    if (ordineDaRiaprire) {
      const loadOrdineCompleto = async () => {
        try {
          const ordine = JSON.parse(ordineDaRiaprire);
          setEditingOrdine(ordine);
          
          // Carica i prodotti completi dal backend per avere tipologie_disponibili e tipologia_default_nome
          const token = localStorage.getItem('token');
          const prodottiResponse = await axios.get('/api/prodotti', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const prodottiCompleti = prodottiResponse.data.data || [];
          
          // Mappa prodotti includendo informazioni complete e tipologia_scelta come tipologia
          // IMPORTANTE: p.id è l'ID della riga ordine_prodotti, p.prodotto_id è l'ID del prodotto nel catalogo
          const prodottiMappati = (ordine.prodotti || []).map(p => {
            const prodottoId = p.prodotto_id || p.id; // Usa prodotto_id se disponibile, altrimenti id
            const prodottoCompleto = prodottiCompleti.find(pc => pc.id === prodottoId);
            
            return {
              ...p,
              id: prodottoId, // SOVRASCRIVI con prodotto_id per usarlo come ID prodotto nel catalogo
              ...prodottoCompleto, // Aggiungi tutte le info del prodotto (tipologie_disponibili, tipologia_default_nome, ecc.)
              tipologia: p.tipologia_scelta || p.tipologia || (prodottoCompleto?.tipologia_default_nome) || prodottoCompleto?.tipologia
            };
          });
          
          setOrdineProdotti(prodottiMappati);
          
          // Carica le note personalizzate se presenti
          if (ordine.note_personalizzate) {
            setNotePersonalizzate(ordine.note_personalizzate);
          }
          
          localStorage.removeItem('ordineDaRiaprire');
          toast.success('Ordine caricato per la modifica');
        } catch (error) {
          console.error('Errore caricamento ordine:', error);
          toast.error('Errore nel caricamento dell\'ordine');
          localStorage.removeItem('ordineDaRiaprire');
        }
      };
      
      loadOrdineCompleto();
    }
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchProdotti();
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchTerm]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [, fornitoriRes] = await Promise.all([
        axios.get('/api/prodotti', { headers }),
        axios.get('/api/fornitori', { headers })
      ]);

      setFornitori(fornitoriRes.data.data);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      toast.error('Errore nel caricamento dei dati');
    }
  };

  const loadSuggerimentiAI = async (data) => {
    if (user?.ruolo !== 'admin') return; // Solo admin
    
    setLoadingSuggerimenti(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/ordini/suggerimenti/${data}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuggerimentiAI(response.data.data);
        console.log('Suggerimenti AI caricati:', response.data.meta);
      }
    } catch (error) {
      console.error('Errore caricamento suggerimenti:', error);
      // Non mostrare errore all'utente, i suggerimenti sono opzionali
    } finally {
      setLoadingSuggerimenti(false);
    }
  };

  const aggiungiSuggerimento = (suggerimento) => {
    // Controlla se esiste già con stesso prodotto_id, fornitore_id e tipologia
    const esisteGia = ordineProdotti.find(p => 
      p.id === suggerimento.prodotto_id && 
      p.fornitore_id === suggerimento.fornitore_id &&
      p.tipologia === suggerimento.tipologia
    );
    
    if (esisteGia) {
      // Se esiste già, incrementa la quantità
      setOrdineProdotti(prev => prev.map(p => 
        p.id === suggerimento.prodotto_id && 
        p.fornitore_id === suggerimento.fornitore_id &&
        p.tipologia === suggerimento.tipologia
          ? { ...p, quantita: p.quantita + Math.round(suggerimento.quantita_suggerita) }
          : p
      ));
      toast.success(`${suggerimento.prodotto_nome} - quantità aggiornata`);
    } else {
      const nuovoProdotto = {
        id: suggerimento.prodotto_id,
        nome: suggerimento.prodotto_nome,
        tipologia: suggerimento.tipologia,
        categoria: suggerimento.categoria,
        quantita: Math.round(suggerimento.quantita_suggerita), // Assicura che sia intero
        note: suggerimento.nota_suggerita,
        fornitore_id: suggerimento.fornitore_id
      };

      setOrdineProdotti(prev => [...prev, nuovoProdotto]);
      toast.success(`${suggerimento.prodotto_nome} aggiunto`);
    }
    
    // Rimuovi dai suggerimenti
    setSuggerimentiAI(prev => prev.filter(s => 
      !(s.prodotto_id === suggerimento.prodotto_id && s.fornitore_id === suggerimento.fornitore_id)
    ));
  };

  const caricaTuttiSuggerimenti = () => {
    let aggiunti = 0;
    let aggiornati = 0;
    
    suggerimentiAI.forEach(sug => {
      // Controlla se esiste già con stesso prodotto_id, fornitore_id e tipologia
      const esisteGia = ordineProdotti.find(p => 
        p.id === sug.prodotto_id && 
        p.fornitore_id === sug.fornitore_id &&
        p.tipologia === sug.tipologia
      );
      
      if (esisteGia) {
        // Se esiste già, incrementa la quantità
        setOrdineProdotti(prev => prev.map(p => 
          p.id === sug.prodotto_id && 
          p.fornitore_id === sug.fornitore_id &&
          p.tipologia === sug.tipologia
            ? { ...p, quantita: p.quantita + Math.round(sug.quantita_suggerita) }
            : p
        ));
        aggiornati++;
      } else {
        const nuovoProdotto = {
          id: sug.prodotto_id,
          nome: sug.prodotto_nome,
          tipologia: sug.tipologia,
          categoria: sug.categoria,
          quantita: Math.round(sug.quantita_suggerita), // Assicura che sia intero
          note: sug.nota_suggerita,
          fornitore_id: sug.fornitore_id
        };
        setOrdineProdotti(prev => [...prev, nuovoProdotto]);
        aggiunti++;
      }
    });
    
    if (aggiunti > 0 || aggiornati > 0) {
      const messaggio = aggiunti > 0 && aggiornati > 0
        ? `${aggiunti} prodotti aggiunti, ${aggiornati} quantità aggiornate`
        : aggiunti > 0
        ? `${aggiunti} prodotti caricati dai suggerimenti`
        : `${aggiornati} quantità aggiornate`;
      toast.success(messaggio);
      setSuggerimentiAI([]);
    }
  };

  const ignoraSuggerimenti = () => {
    setSuggerimentiAI([]);
    toast.success('Suggerimenti AI ignorati');
  };

  // Effect per caricare suggerimenti quando cambia la data
  useEffect(() => {
    if (dataOrdine && user?.ruolo === 'admin' && !editingOrdine) {
      loadSuggerimentiAI(dataOrdine);
    }
  }, [dataOrdine, user]);

  const searchProdotti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/prodotti/search?q=${searchTerm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSearchResults(response.data.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
    }
  };

  // Gestisce i prodotti parsati dall'AI
  const handleProdottiParsed = async (prodottiParsati) => {
    console.log('🤖 [AI] handleProdottiParsed chiamata con:', prodottiParsati);
    
    if (!prodottiParsati || prodottiParsati.length === 0) {
      console.log('❌ [AI] Nessun prodotto da aggiungere');
      toast.error('Nessun prodotto da aggiungere');
      return;
    }

    console.log(`🤖 [AI] Inizio aggiunta di ${prodottiParsati.length} prodotti`);
    let aggiunti = 0;
    
    // Carica tutti i prodotti dal catalogo se non già caricati
    let catalogoProdotti = [];
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/prodotti', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        catalogoProdotti = response.data.data;
        console.log(`🤖 [AI] Catalogo prodotti caricato: ${catalogoProdotti.length} prodotti`);
      }
    } catch (error) {
      console.error('❌ [AI] Errore caricamento catalogo:', error);
      toast.error('Errore nel caricamento del catalogo prodotti');
      return;
    }
    
    for (const prodotto of prodottiParsati) {
      console.log(`🤖 [AI] Processo prodotto:`, {
        id: prodotto.id,
        nome: prodotto.nome,
        quantita: prodotto.quantita,
        note: prodotto.note,
        tipologia: prodotto.tipologia
      });
      
      try {
        // Trova il prodotto completo nel catalogo
        const prodottoCompleto = catalogoProdotti.find(p => p.id === prodotto.id);
        
        if (!prodottoCompleto) {
          console.error(`❌ [AI] Prodotto ID ${prodotto.id} non trovato nel catalogo`);
          continue;
        }
        
        console.log(`🤖 [AI] Prodotto trovato nel catalogo:`, prodottoCompleto);
        
        // Trova fornitore default per questa categoria
        const fornitoreDefault = await getFornitoreDefault(prodottoCompleto.categoria);
        console.log(`🤖 [AI] Fornitore default:`, fornitoreDefault);
        
        // Trova tipologia finale
        // Se la tipologia è null, undefined, o 'da_definire', usa quella del catalogo
        let tipologiaFinale = null;
        if (prodotto.tipologia && prodotto.tipologia !== 'da_definire' && prodotto.tipologia !== 'N/A') {
          tipologiaFinale = prodotto.tipologia;
        } else {
          tipologiaFinale = prodottoCompleto.tipologia_default_nome || prodottoCompleto.tipologia || null;
        }
        console.log(`🤖 [AI] Tipologia finale:`, tipologiaFinale);
        
        const nuovoProdotto = {
          ...prodottoCompleto,
          quantita: prodotto.quantita,
          note: prodotto.note || prodottoCompleto.nota_default || '',
          tipologia: tipologiaFinale,
          tipologie_disponibili: prodottoCompleto.tipologie_disponibili || [],
          tipologia_default_nome: prodottoCompleto.tipologia_default_nome || null,
          fornitore_id: fornitoreDefault?.id || null,
          uniqueId: `${prodotto.id}-${Date.now()}-${Math.random()}` // ID univoco per React key
        };
        
        console.log(`🤖 [AI] Aggiungo prodotto:`, nuovoProdotto);
        
        // Aggiungi alla lista
        setOrdineProdotti(prev => {
          const updated = [...prev, nuovoProdotto];
          console.log(`🤖 [AI] Lista aggiornata, totale: ${updated.length}`);
          return updated;
        });
        
        aggiunti++;
        console.log(`✅ [AI] Prodotto aggiunto (${aggiunti}/${prodottiParsati.length})`);
      } catch (error) {
        console.error(`❌ [AI] Errore aggiunta prodotto ${prodotto.nome}:`, error);
      }
    }
    
    console.log(`🤖 [AI] Completato! Prodotti aggiunti: ${aggiunti}/${prodottiParsati.length}`);
    
    if (aggiunti > 0) {
      toast.success(`${aggiunti} prodotti aggiunti all'ordine`);
    } else {
      toast.error('Nessun prodotto aggiunto');
    }
  };

  const addProdottoToOrdine = async (prodotto) => {
    const tipologieDisponibili = prodotto.tipologie_disponibili || [];
    
    // Se c'è una tipologia default, usala automaticamente anche se ci sono più tipologie disponibili
    if (prodotto.tipologia_default_nome) {
      const tipologiaScelta = prodotto.tipologia_default_nome;
      await aggiungiProdottoConTipologia(prodotto, tipologiaScelta);
      return;
    }
    
    // Se non c'è default ma ci sono più tipologie disponibili, chiedi quale usare
    if (tipologieDisponibili.length > 1) {
      // Mostra modal per scegliere tipologia
      setProdottoPendingTipologia(prodotto);
      setShowTipologiaModal(true);
      return;
    }
    
    // Se ha una sola tipologia, usala direttamente
    if (tipologieDisponibili.length === 1) {
      const tipologiaScelta = tipologieDisponibili[0].nome;
      await aggiungiProdottoConTipologia(prodotto, tipologiaScelta);
      return;
    }
    
    // Se non ci sono tipologie disponibili, usa quella default o quella del prodotto
    const tipologiaScelta = prodotto.tipologia_default_nome || prodotto.tipologia;
    
    // Se ancora non c'è tipologia, non impostare 'N/A' ma lascia vuoto (sarà gestito dal backend)
    if (!tipologiaScelta || tipologiaScelta === 'N/A' || tipologiaScelta === 'da_definire') {
      // Se il prodotto non ha tipologie, aggiungilo comunque senza tipologia
      await aggiungiProdottoConTipologia(prodotto, null);
      return;
    }
    
    await aggiungiProdottoConTipologia(prodotto, tipologiaScelta);
  };

  const aggiungiProdottoConTipologia = async (prodotto, tipologiaScelta) => {
    // Trova fornitore default per questa categoria
    const fornitoreDefault = await getFornitoreDefault(prodotto.categoria);
    const fornitoreIdFinale = fornitoreDefault?.id || null;
    
    let tipologiaFinale = tipologiaScelta;
    
    // Assicurati che tipologiaScelta sia sempre valorizzata se ci sono tipologie disponibili
    if (!tipologiaFinale && prodotto.tipologie_disponibili && prodotto.tipologie_disponibili.length > 0) {
      // Se non c'è tipologia scelta ma ci sono tipologie disponibili, usa la prima o la default
      tipologiaFinale = prodotto.tipologia_default_nome || prodotto.tipologie_disponibili[0].nome;
    }
    if (!tipologiaFinale) {
      tipologiaFinale = prodotto.tipologia_default_nome || prodotto.tipologia;
    }
    
    // Controlla se esiste già con stesso prodotto_id, fornitore_id e tipologia
    const existingIndex = ordineProdotti.findIndex(item => 
      item.id === prodotto.id && 
      item.fornitore_id === fornitoreIdFinale &&
      item.tipologia === tipologiaFinale
    );
    
    let updated;
    
    if (existingIndex >= 0) {
      // Se esiste già, incrementa la quantità
      updated = [...ordineProdotti];
      updated[existingIndex].quantita += 1;
      tipologiaFinale = updated[existingIndex].tipologia;
      // Segna questo prodotto come ultimo modificato
      setUltimoProdottoInserito(prodotto.id);
    } else {
      // Nuovo prodotto
      updated = [...ordineProdotti, { 
        ...prodotto, 
        quantita: 1, 
        note: prodotto.nota_default || '',
        tipologia: tipologiaFinale,
        tipologie_disponibili: prodotto.tipologie_disponibili || [],
        tipologia_default_nome: prodotto.tipologia_default_nome || null,
        fornitore_id: fornitoreIdFinale
      }];
      
      // Segna questo prodotto come ultimo inserito
      setUltimoProdottoInserito(prodotto.id);
    }
    
    setOrdineProdotti(updated);
    setSearchTerm('');
    setShowSearchResults(false);
    
    // Rimuovi dai suggerimenti se corrisponde a un suggerimento (stesso prodotto_id, fornitore_id e tipologia)
    setSuggerimentiAI(prev => prev.filter(s => 
      !(s.prodotto_id === prodotto.id && 
        s.fornitore_id === fornitoreIdFinale &&
        s.tipologia === tipologiaFinale)
    ));
    
    // Debug: verifica che la tipologia sia stata impostata
    console.log('🔍 Prodotto aggiunto:', {
      nome: prodotto.nome,
      tipologiaScelta: tipologiaScelta,
      tipologiaFinale: updated[updated.length - 1]?.tipologia,
      fornitore_id: fornitoreIdFinale,
      tipologieDisponibili: prodotto.tipologie_disponibili?.map(t => t.nome)
    });
    
    toast.success(`${prodotto.nome} (${tipologiaFinale || tipologiaScelta || 'N/A'}) aggiunto all'ordine`);

    // Riporta il focus sul campo ricerca dopo il prossimo render (useEffect sopra)
    setFocusSearchAfterAdd(true);
  };

  const handleSelezioneTipologia = (tipologiaNome) => {
    if (prodottoPendingTipologia) {
      aggiungiProdottoConTipologia(prodottoPendingTipologia, tipologiaNome);
      setShowTipologiaModal(false);
      setProdottoPendingTipologia(null);
    }
  };
  
  const getFornitoreDefault = async (categoria) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/fornitori/default/${categoria}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      // Se non c'è un default, prendi il primo fornitore disponibile per questa categoria
      const fornitoriResponse = await axios.get(`/api/fornitori/categoria/${categoria}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (fornitoriResponse.data.success && fornitoriResponse.data.data.length > 0) {
        return fornitoriResponse.data.data[0];
      }
      
      return null;
    } catch (error) {
      console.error('Errore recupero fornitore default:', error);
      return null;
    }
  };
  
  const updateFornitore = (prodottoId, fornitoreId) => {
    const updated = ordineProdotti.map(item =>
      item.id === prodottoId ? { ...item, fornitore_id: parseInt(fornitoreId) } : item
    );
    setOrdineProdotti(updated);
  };

  const updateQuantita = (prodottoId, newQuantita) => {
    if (newQuantita <= 0) {
      removeProdottoFromOrdine(prodottoId);
      return;
    }

    const updated = ordineProdotti.map(item =>
      item.id === prodottoId ? { ...item, quantita: newQuantita } : item
    );
    setOrdineProdotti(updated);
    // Se hai modificato la quantità dell'ultimo prodotto, riporta il focus su "Cerca prodotti"
    if (prodottoId === ultimoProdottoInserito) {
      setFocusSearchAfterAdd(true);
    }
  };

  const removeProdottoFromOrdine = (prodottoIdOrUniqueId) => {
    // Cerca per uniqueId (se presente) o per id
    const prodotto = ordineProdotti.find(item => 
      (item.uniqueId && item.uniqueId === prodottoIdOrUniqueId) || 
      (!item.uniqueId && item.id === prodottoIdOrUniqueId)
    );
    setProdottoToDelete(prodotto);
    setShowDeleteProdottoModal(true);
  };

  const executeRemoveProdotto = () => {
    if (!prodottoToDelete) return;
    // Rimuovi per uniqueId se presente, altrimenti per id
    const updated = ordineProdotti.filter(item => {
      if (prodottoToDelete.uniqueId && item.uniqueId) {
        return item.uniqueId !== prodottoToDelete.uniqueId;
      }
      return item.id !== prodottoToDelete.id;
    });
    setOrdineProdotti(updated);
    toast.success('Prodotto rimosso dall\'ordine');
    setShowDeleteProdottoModal(false);
    setProdottoToDelete(null);
  };



  const updateNote = (prodottoId, note) => {
    const updated = ordineProdotti.map(item =>
      item.id === prodottoId ? { ...item, note } : item
    );
    setOrdineProdotti(updated);
  };

  const groupByCategoria = () => {
    const grouped = {};
    ordineProdotti.forEach(item => {
      if (!grouped[item.categoria]) {
        grouped[item.categoria] = [];
      }
      grouped[item.categoria].push(item);
    });
    
    // Ordina i prodotti alfabeticamente per ogni categoria
    Object.keys(grouped).forEach(categoria => {
      grouped[categoria].sort((a, b) => a.nome.localeCompare(b.nome));
    });
    
    return grouped;
  };
  
  const getUltimoProdottoInserito = () => {
    if (!ultimoProdottoInserito) return null;
    return ordineProdotti.find(item => item.id === ultimoProdottoInserito);
  };

  const groupByFornitore = () => {
    const grouped = {};
    // Escludi l'ultimo prodotto inserito dalla lista normale
    const prodottiDaMostrare = ordineProdotti.filter(item => item.id !== ultimoProdottoInserito);
    
    prodottiDaMostrare.forEach(item => {
      const fornitoreId = item.fornitore_id || 'senza_fornitore';
      if (!grouped[fornitoreId]) {
        grouped[fornitoreId] = {
          fornitore_id: item.fornitore_id,
          fornitore: fornitori.find(f => f.id === item.fornitore_id),
          prodotti: []
        };
      }
      grouped[fornitoreId].prodotti.push(item);
    });
    
    // Ordina i prodotti alfabeticamente per ogni fornitore
    Object.keys(grouped).forEach(fornitoreId => {
      grouped[fornitoreId].prodotti.sort((a, b) => a.nome.localeCompare(b.nome));
    });
    
    return grouped;
  };

  const handleNotaChange = (categoria, nota) => {
    setNotePersonalizzate({
      ...notePersonalizzate,
      [categoria]: nota
    });
  };

  /** Salvataggio in background (auto-save). Non naviga, mostra toast "Bozza salvata/aggiornata". */
  const saveOrdineInBackground = async () => {
    if (ordineProdotti.length === 0) return;
    const token = localStorage.getItem('token');
    const prodottiDaSalvare = ordineProdotti.map(item => ({
      id: item.prodotto_id || item.id,
      quantita: item.quantita,
      note: item.note || '',
      fornitore_id: item.fornitore_id || null,
      tipologia: (item.tipologia && item.tipologia !== 'N/A' && item.tipologia !== 'da_definire') ? item.tipologia : null
    }));
    try {
      if (editingOrdine) {
        const response = await axios.put(`/api/ordini/${editingOrdine.id}`, {
          prodotti: prodottiDaSalvare,
          note_personalizzate: notePersonalizzate
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          toast.success('Bozza aggiornata', { duration: 2000 });
        }
      } else {
        const response = await axios.post('/api/ordini', {
          prodotti: prodottiDaSalvare,
          data_ordine: dataOrdine,
          note_personalizzate: notePersonalizzate
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success && response.data.id) {
          setEditingOrdine({ id: response.data.id, data_ordine: dataOrdine });
          toast.success('Bozza salvata', { duration: 2000 });
        }
      }
    } catch (error) {
      if (error.response?.data?.code === 'ORDINE_ESISTENTE') {
        hasCreatedDraftRef.current = false;
        setOrdineEsistenteId(error.response.data.ordineEsistenteId);
        setShowOrdineEsistenteModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Errore nel salvataggio della bozza');
      }
    }
  };

  // Mantieni la ref aggiornata per il timeout di auto-save (evita "before initialization")
  useEffect(() => {
    saveOrdineInBackgroundRef.current = saveOrdineInBackground;
  });

  // Compatta il campo data ordine quando c'è almeno un prodotto (per guadagnare spazio)
  useEffect(() => {
    if (editingOrdine || ordineProdotti.length < 1) return;
    setDataOrdineCollapsed(true);
  }, [editingOrdine, ordineProdotti.length]);

  // Auto-save: al raggiungimento di 5 prodotti crea la bozza (solo in nuovo ordine, non in modifica)
  useEffect(() => {
    if (editingOrdine || ordineProdotti.length < 5 || hasCreatedDraftRef.current) return;
    hasCreatedDraftRef.current = true;
    saveOrdineInBackground();
  }, [editingOrdine, ordineProdotti.length]);

  // Auto-save: mentre modifichi la bozza, salva in background ogni 2 secondi dopo l'ultima modifica
  useEffect(() => {
    if (!editingOrdine || ordineProdotti.length === 0) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      autoSaveDebounceRef.current = null;
      saveOrdineInBackgroundRef.current?.();
    }, 2000);
    return () => {
      if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    };
  }, [editingOrdine, ordineProdotti, notePersonalizzate]);

  const createOrdine = async () => {
    if (ordineProdotti.length === 0) {
      toast.error('Aggiungi almeno un prodotto all\'ordine');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const prodottiDaSalvare = ordineProdotti.map(item => ({
        id: item.prodotto_id || item.id,
        quantita: item.quantita,
        note: item.note || '',
        fornitore_id: item.fornitore_id || null,
        tipologia: (item.tipologia && item.tipologia !== 'N/A' && item.tipologia !== 'da_definire') ? item.tipologia : null
      }));

      if (editingOrdine) {
        const response = await axios.put(`/api/ordini/${editingOrdine.id}`, {
          prodotti: prodottiDaSalvare,
          note_personalizzate: notePersonalizzate
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          toast.success('Ordine aggiornato con successo!');
          navigate('/ordini');
        }
      } else {
        const response = await axios.post('/api/ordini', {
          prodotti: prodottiDaSalvare,
          data_ordine: dataOrdine,
          note_personalizzate: notePersonalizzate
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          toast.success('Ordine creato con successo!');
          navigate('/ordini');
        }
      }
    } catch (error) {
      console.error('❌ [NUOVO_ORDINE] Errore salvataggio ordine:', error);
      if (error.response?.data?.code === 'ORDINE_ESISTENTE') {
        setOrdineEsistenteId(error.response.data.ordineEsistenteId);
        setShowOrdineEsistenteModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Errore nel salvataggio dell\'ordine');
      }
    } finally {
      setLoading(false);
    }
  };

  const apriOrdineEsistente = () => {
    setShowOrdineEsistenteModal(false);
    // Svuota i prodotti correnti e vai a modificare l'ordine esistente
    navigate('/ordini');
    setTimeout(() => {
      toast('Cerca l\'ordine bozza nella lista e modificalo da lì', {
        icon: 'ℹ️',
        duration: 4000
      });
    }, 500);
  };

  const handleInviaWhatsAppSingolo = async (linkData) => {
    const token = localStorage.getItem('token');
    
    try {
      // Apri il link WhatsApp
      window.open(linkData.link, '_blank');
      
      // Marca come inviato nel database
      await axios.put(`/api/whatsapp/marca-inviato/${linkData.messaggio_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiorna lo stato locale
      const updated = whatsappLinks.map(link =>
        link.messaggio_id === linkData.messaggio_id ? { ...link, inviato: true } : link
      );
      setWhatsappLinks(updated);

      // Se con questo invio tutti i messaggi sono stati inviati, passa l'ordine a "inviato"
      if (updated.every(link => link.inviato) && ordineIdWhatsApp) {
        try {
          await axios.put(`/api/ordini/${ordineIdWhatsApp}/stato`, { stato: 'sent' }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Tutti i messaggi inviati. Ordine segnato come Inviato.', { duration: 3000 });
        } catch (err) {
          console.error('Errore aggiornamento stato ordine:', err);
        }
      }
      
      toast.success(`Messaggio aperto per ${linkData.fornitore}`, { duration: 2000 });
    } catch (error) {
      console.error('Errore registrazione invio:', error);
      toast.error('Errore nella registrazione dell\'invio');
    }
  };

  const handleChiudiModalWhatsApp = async () => {
    // Controlla se ci sono messaggi non inviati
    const nonInviati = whatsappLinks.filter(link => !link.inviato);
    
    if (nonInviati.length > 0) {
      const conferma = window.confirm(
        `Attenzione! Hai ${nonInviati.length} messaggi non ancora inviati:\n\n` +
        nonInviati.map(link => `- ${link.fornitore}`).join('\n') +
        `\n\nSei sicuro di voler chiudere? Potrai sempre inviare i messaggi rimanenti dalla lista ordini.`
      );
      
      if (!conferma) return;
    }
    
    // Verifica se tutti i messaggi sono stati inviati
    const tuttiInviati = whatsappLinks.every(link => link.inviato);
    
    if (tuttiInviati && ordineIdWhatsApp) {
      // Aggiorna lo stato dell'ordine a 'sent'
      const token = localStorage.getItem('token');
      try {
        await axios.put(`/api/ordini/${ordineIdWhatsApp}/stato`, {
          stato: 'sent'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Stato ordine aggiornato a "sent"');
      } catch (error) {
        console.error('❌ Errore aggiornamento stato ordine:', error);
      }
    }
    
    setShowWhatsAppLinksModal(false);
    setWhatsappLinks([]);
    setOrdineIdWhatsApp(null);
    navigate('/ordini');
  };

  const rigeneraMessaggiWhatsApp = async () => {
    if (!ordineIdWhatsApp) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Usa i dati dell'ordine corrente (ordineProdotti e notePersonalizzate)
      const groupedByFornitore = groupByFornitore();
      const linkWhatsApp = [];
      
      for (const gruppo of Object.values(groupedByFornitore)) {
        try {
          if (!gruppo.fornitore) {
            console.warn(`Prodotti senza fornitore assegnato trovati`);
            continue;
          }

          // Prendi la nota specifica per questo fornitore
          const notaFornitore = notePersonalizzate[`fornitore_${gruppo.fornitore.id}`] || '';

          // Genera messaggio e link (il backend eliminerà automaticamente i pending esistenti)
          // intro/outro vengono presi automaticamente dal database del fornitore
          const messaggioResponse = await axios.post('/api/whatsapp/genera-messaggio', {
            ordine_id: ordineIdWhatsApp,
            fornitore_id: gruppo.fornitore.id,
            nota_personalizzata: notaFornitore
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (messaggioResponse.data.success) {
            const messaggioData = messaggioResponse.data.data;
            linkWhatsApp.push({
              messaggio_id: messaggioData.messaggio_id,
              fornitore_id: gruppo.fornitore.id,
              fornitore: gruppo.fornitore.nome,
              link: messaggioData.whatsappLink,
              messaggio: messaggioData.messaggio,
              inviato: false
            });
          }
        } catch (error) {
          console.error(`Errore rigenerazione link per fornitore ${gruppo.fornitore?.nome}:`, error);
        }
      }

      if (linkWhatsApp.length > 0) {
        setWhatsappLinks(linkWhatsApp);
        toast.success(`${linkWhatsApp.length} messaggi rigenerati con successo!`, { duration: 3000 });
      } else {
        toast.error('Nessun messaggio rigenerato');
      }
    } catch (error) {
      console.error('Errore rigenerazione messaggi:', error);
      toast.error('Errore nella rigenerazione dei messaggi WhatsApp');
    }
  };

  const sendWhatsAppMessages = async () => {
    if (ordineProdotti.length === 0) {
      toast.error('Aggiungi almeno un prodotto all\'ordine');
      return;
    }

    // Mostra modale di conferma
    setConfirmAction(() => () => executeSendWhatsApp());
    setShowConfirmModal(true);
  };

  const executeSendWhatsApp = async () => {
    setShowConfirmModal(false);

    setSendingWhatsApp(true);

      try {
        const token = localStorage.getItem('token');
        let ordineId;

        if (editingOrdine) {
          // Aggiorna ordine esistente
          const updateResponse = await axios.put(`/api/ordini/${editingOrdine.id}`, {
            prodotti: ordineProdotti.map(item => ({
              id: item.prodotto_id || item.id, // Usa prodotto_id se disponibile, altrimenti id
              quantita: item.quantita,
              note: item.note || '',
              fornitore_id: item.fornitore_id || null,
              tipologia: (item.tipologia && item.tipologia !== 'N/A' && item.tipologia !== 'da_definire') ? item.tipologia : null
            })),
            note_personalizzate: notePersonalizzate
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!updateResponse.data.success) {
            throw new Error('Errore nell\'aggiornamento dell\'ordine');
          }

          ordineId = editingOrdine.id;
        } else {
          // Crea nuovo ordine
          const ordineResponse = await axios.post('/api/ordini', {
            prodotti: ordineProdotti.map(item => ({
              id: item.prodotto_id || item.id, // Usa prodotto_id se disponibile, altrimenti id
              quantita: item.quantita,
              note: item.note || '',
              fornitore_id: item.fornitore_id || null,
              tipologia: (item.tipologia && item.tipologia !== 'N/A' && item.tipologia !== 'da_definire') ? item.tipologia : null
            })),
            data_ordine: dataOrdine,
            note_personalizzate: notePersonalizzate
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!ordineResponse.data.success) {
            throw new Error('Errore nella creazione dell\'ordine');
          }

          ordineId = ordineResponse.data.id;
        }

      // Debug: verifica tipologie prima di generare messaggi
      console.log('🔍 Ordine salvato. Prodotti con tipologie:', ordineProdotti.map(p => ({
        nome: p.nome,
        tipologia: p.tipologia,
        quantita: p.quantita
      })));
      
      // Poi genera i link WhatsApp per fornitore
      const groupedByFornitore = groupByFornitore();
      const linkWhatsApp = [];
      let successCount = 0;
      
      for (const gruppo of Object.values(groupedByFornitore)) {
        try {
          if (!gruppo.fornitore) {
            console.warn(`Prodotti senza fornitore assegnato trovati`);
            continue;
          }

          // Prendi la nota specifica per questo fornitore
          const notaFornitore = notePersonalizzate[`fornitore_${gruppo.fornitore.id}`] || '';

          // Genera messaggio e link (intro/outro vengono presi automaticamente dal database del fornitore)
          const messaggioResponse = await axios.post('/api/whatsapp/genera-messaggio', {
            ordine_id: ordineId,
            fornitore_id: gruppo.fornitore.id,
            nota_personalizzata: notaFornitore
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (messaggioResponse.data.success) {
            const messaggioData = messaggioResponse.data.data;
            linkWhatsApp.push({
              messaggio_id: messaggioData.messaggio_id,
              fornitore_id: gruppo.fornitore.id,
              fornitore: gruppo.fornitore.nome,
              link: messaggioData.whatsappLink,
              messaggio: messaggioData.messaggio,
              inviato: false
            });
            successCount++;
          }
        } catch (error) {
          console.error(`Errore generazione link per fornitore ${gruppo.fornitore?.nome}:`, error);
        }
      }

      if (linkWhatsApp.length > 0) {
        // Salva i link e mostra il modal con i pulsanti
        setWhatsappLinks(linkWhatsApp);
        setOrdineIdWhatsApp(ordineId);
        setShowWhatsAppLinksModal(true);
        toast.success(`${linkWhatsApp.length} messaggi pronti da inviare!`, { duration: 3000 });
      } else {
        toast.error('Nessun messaggio WhatsApp generato');
      }
    } catch (error) {
      console.error('Errore invio WhatsApp:', error);
      
      // Gestisci caso di ordine già esistente per la data
      if (error.response?.data?.code === 'ORDINE_ESISTENTE') {
        setOrdineEsistenteId(error.response.data.ordineEsistenteId);
        setShowOrdineEsistenteModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Errore nell\'invio dei messaggi WhatsApp');
      }
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const groupedByFornitore = groupByFornitore();

  return (
    <div className="space-y-3 sm:space-y-6 pb-6">
      {/* Header - compatto su mobile */}
      <div>
        {/* <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
          {editingOrdine ? `Modifica Ordine #${editingOrdine.id}` : 'Nuovo Ordine'}
        </h1> */}
      </div>

      {/* Quick Order Input con AI - nascosto temporaneamente */}
      {false && (
        <QuickOrderInput 
          onProdottiParsed={handleProdottiParsed}
          token={localStorage.getItem('token')}
        />
      )}

      {/* Data Ordine - collassabile dopo selezione */}
      {!editingOrdine && (
        dataOrdineCollapsed ? (
          // Versione compatta collassata
          <div className="bg-white shadow rounded-lg p-2 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Data Ordine:</span>
                <span className="text-sm sm:text-base font-semibold text-gray-900">
                  {new Date(dataOrdine + 'T12:00:00').toLocaleDateString('it-IT', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <button
                onClick={() => setDataOrdineCollapsed(false)}
                className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium px-2 py-1"
              >
                Modifica
              </button>
            </div>
          </div>
        ) : (
          // Versione espansa completa
          <div className="bg-white shadow rounded-lg p-3 sm:p-6">
            <label htmlFor="dataOrdine" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Data Ordine
            </label>
            <input
              type="date"
              id="dataOrdine"
              value={dataOrdine}
              onChange={(e) => {
                setDataOrdine(e.target.value);
                // Collassa automaticamente dopo la selezione (solo su mobile)
                if (window.innerWidth < 640) {
                  setTimeout(() => setDataOrdineCollapsed(true), 300);
                }
              }}
              onBlur={() => {
                // Collassa quando perde il focus su mobile
                if (window.innerWidth < 640) {
                  setDataOrdineCollapsed(true);
                }
              }}
              onClick={(e) => {
                // Su mobile, apri il calendario immediatamente
                if (e.target.showPicker && typeof e.target.showPicker === 'function') {
                  e.target.showPicker();
                }
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 py-2.5 border min-h-[44px] cursor-pointer"
              style={{
                fontSize: '16px', // Previene zoom automatico su iOS
                WebkitAppearance: 'none',
                MozAppearance: 'textfield'
              }}
            />
            {/* <p className="mt-1 text-xs text-gray-500">
              Puoi creare ordini per oggi o per giorni futuri (es. weekend, inizio settimana)
            </p> */}
          </div>
        )
      )}

      {/* Box Suggerimenti AI - nascosto temporaneamente
      {user?.ruolo === 'admin' && !editingOrdine && showSuggerimenti && suggerimentiAI.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-3 sm:p-6 shadow-sm">
          <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="flex items-center min-w-0 flex-1">
              <div className="h-8 w-8 sm:h-10 sm:w-10 bg-blue-600 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                <span className="text-white text-base sm:text-lg">🤖</span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Suggerimenti AI
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">
                  Basato su ordini di{' '}
                  <span className="font-medium">{new Date(dataOrdine + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long' })}</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSuggerimenti(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {loadingSuggerimenti ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-4">
                {suggerimentiAI.slice(0, 5).map((sug) => (
                  <div
                    key={`${sug.prodotto_id}-${sug.fornitore_id}`}
                    className="bg-white rounded-lg p-3 flex items-center justify-between hover:shadow-md transition-shadow"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 flex-wrap">
                        <span className="font-medium text-gray-900">{sug.prodotto_nome}</span>
                        <span className="text-sm text-gray-500">
                          ({sug.fornitore_nome})
                        </span>
                        {sug.affidabilita === 'alta' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {sug.percentuale}% frequenza
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        <span className="font-medium">{sug.quantita_suggerita} {sug.tipologia}</span>
                        {sug.nota_suggerita && (
                          <span className="ml-2 text-gray-500">• {sug.nota_suggerita}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => aggiungiSuggerimento(sug)}
                      className="ml-4 btn btn-sm bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                    >
                      <Plus className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Aggiungi</span>
                    </button>
                  </div>
                ))}
              </div>

              {suggerimentiAI.length > 5 && (
                <p className="text-sm text-gray-500 mb-3">
                  ...e altri {suggerimentiAI.length - 5} prodotti suggeriti
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-blue-200 gap-2">
                <p className="text-sm text-gray-600">
                  💡 Totale: <strong>{suggerimentiAI.length} prodotti suggeriti</strong>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={ignoraSuggerimenti}
                    className="btn btn-outline flex items-center text-sm"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Ignora
                  </button>
                  <button
                    onClick={caricaTuttiSuggerimenti}
                    className="btn btn-primary flex items-center"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Carica Tutto
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )} */}

      {/* Mostra link per riaprire se chiuso - nascosto temporaneamente
      {user?.ruolo === 'admin' && !editingOrdine && !showSuggerimenti && suggerimentiAI.length > 0 && (
        <button
          onClick={() => setShowSuggerimenti(true)}
          className="w-full py-2 px-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center justify-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Mostra suggerimenti AI ({suggerimentiAI.length})
        </button>
      )} */}

      {/* Search - sticky su mobile quando tastiera aperta */}
      <div className="bg-white shadow rounded-lg p-3 sm:p-6 sticky top-0 sm:static z-10">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          </div>
          <input
            ref={searchRef}
            type="text"
            className="block w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="Cerca prodotti..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: '16px' }} // Previene zoom su iOS
          />
        </div>

        {/* Search Results - visibile sopra tastiera */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-lg max-h-[calc(100vh-200px)] sm:max-h-60 overflow-y-auto">
            {searchResults.map((prodotto) => (
              <button
                key={prodotto.id}
                onClick={() => addProdottoToOrdine(prodotto)}
                className="w-full px-4 py-2.5 sm:py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 active:bg-gray-100"
                style={{ touchAction: 'manipulation' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{prodotto.nome}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {prodotto.tipologia} • {prodotto.categoria}
                    </p>
                  </div>
                  <Plus className="h-4 w-4 text-blue-600 flex-shrink-0 ml-2" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Order Summary */}
      {ordineProdotti.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-3 py-2 sm:px-4 sm:py-2.5 border-b border-gray-200">
            <h2 className="text-sm sm:text-base font-medium text-gray-900 flex items-center">
              <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5" />
              Ordine ({ordineProdotti.length} prodotti)
            </h2>
          </div>

          {/* Box Ultimo Prodotto Inserito - solo sfondo blu, senza titolo (recupero spazio) */}
          {getUltimoProdottoInserito() && (() => {
            const ultimoProdotto = getUltimoProdottoInserito();
            const fornitoriCategoria = fornitori.filter(f => 
              Array.isArray(f.categorie) && f.categorie.includes(ultimoProdotto.categoria)
            );
            
            return (
              <div className="p-2.5 sm:p-4 bg-blue-50 border-b-2 border-blue-300">
                <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm">
                  {/* Nome prodotto con cestino a destra */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm font-medium text-gray-900 flex-1 break-words">{ultimoProdotto.nome}</p>
                    {/* Cestino piccolo a destra del nome */}
                    <button
                      onClick={() => removeProdottoFromOrdine(ultimoProdotto.id)}
                      className="flex items-center justify-center min-w-[32px] min-h-[32px] sm:min-w-[28px] sm:min-h-[28px] p-1.5 sm:p-1 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                      style={{ touchAction: 'manipulation' }}
                      aria-label="Rimuovi prodotto"
                    >
                      <Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                    </button>
                  </div>
                  
                  <div className="flex items-start justify-between gap-3">
                    {/* Tipologia, fornitore e note - con più spazio */}
                    <div className="flex-1 min-w-0">
                      {/* Selezione tipologia - mostra sempre dropdown se ci sono tipologie disponibili */}
                      {ultimoProdotto.tipologie_disponibili && ultimoProdotto.tipologie_disponibili.length > 0 ? (
                        <select
                          value={ultimoProdotto.tipologia || ultimoProdotto.tipologia_default_nome || (ultimoProdotto.tipologie_disponibili && ultimoProdotto.tipologie_disponibili.length > 0 ? ultimoProdotto.tipologie_disponibili[0].nome : '')}
                          onChange={(e) => {
                            const nuovaTipologia = e.target.value;
                            const updated = ordineProdotti.map(p =>
                              p.id === ultimoProdotto.id ? { ...p, tipologia: nuovaTipologia } : p
                            );
                            setOrdineProdotti(updated);
                          }}
                          className="text-xs border border-blue-300 rounded px-2 py-1 w-full bg-white focus:ring-1 focus:ring-blue-500"
                        >
                          {ultimoProdotto.tipologie_disponibili.map(tip => (
                            <option key={tip.id || tip.nome} value={tip.nome}>
                              {tip.nome}
                            </option>
                          ))}
                        </select>
                      ) : ultimoProdotto.tipologia && ultimoProdotto.tipologia !== 'da_definire' ? (
                        <p className="text-xs text-gray-500">{ultimoProdotto.tipologia}</p>
                      ) : (
                        <p className="text-xs text-gray-400 italic">Nessuna tipologia selezionata</p>
                      )}
                      
                      {/* Dropdown Fornitore */}
                      {fornitoriCategoria.length > 1 && (
                        <select
                          value={ultimoProdotto.fornitore_id || ''}
                          onChange={(e) => updateFornitore(ultimoProdotto.id, e.target.value)}
                          className="mt-1 text-xs border border-blue-300 rounded px-2 py-1 w-full bg-white focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Seleziona fornitore...</option>
                          {fornitoriCategoria.map(f => (
                            <option key={f.id} value={f.id}>
                              {f.nome}
                            </option>
                          ))}
                        </select>
                      )}
                      
                      {fornitoriCategoria.length === 1 && (
                        <p className="mt-1 text-xs text-blue-600">
                          Fornitore: {fornitoriCategoria[0].nome}
                        </p>
                      )}
                      
                      <input
                        type="text"
                        placeholder="Note (opzionale)"
                        value={ultimoProdotto.note || ''}
                        onChange={(e) => updateNote(ultimoProdotto.id, e.target.value)}
                        className="mt-1 text-xs border border-gray-300 rounded px-2 py-1 w-full"
                      />
                    </div>
                    
                    {/* Controlli quantità - a destra */}
                    <div className="flex items-center flex-shrink-0">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => updateQuantita(ultimoProdotto.id, ultimoProdotto.quantita - 1)}
                          className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                          style={{ touchAction: 'manipulation' }}
                          aria-label="Diminuisci quantità"
                        >
                          <Minus className="h-5 w-5 sm:h-4 sm:w-4" />
                        </button>
                        <span className="text-base sm:text-sm font-medium text-gray-900 min-w-[3rem] sm:min-w-[2rem] text-center">
                          {ultimoProdotto.quantita}
                        </span>
                        <button
                          onClick={() => updateQuantita(ultimoProdotto.id, ultimoProdotto.quantita + 1)}
                          className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                          style={{ touchAction: 'manipulation' }}
                          aria-label="Aumenta quantità"
                        >
                          <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="divide-y divide-gray-200">
            {Object.entries(groupedByFornitore).map(([fornitoreId, gruppo]) => {
              if (!gruppo.fornitore) return null;
              
              return (
                <div key={fornitoreId} className="p-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Package className="h-4 w-4 mr-2" />
                    {gruppo.fornitore.nome}
                  </h3>
                
                <div className="space-y-3">
                  {gruppo.prodotti.map((item, itemIndex) => {
                    // Filtra fornitori per questa categoria
                    const fornitoriCategoria = fornitori.filter(f => 
                      Array.isArray(f.categorie) && f.categorie.includes(item.categoria)
                    );
                    
                    return (
                      <div key={item.uniqueId || `${item.id}-${gruppo.fornitore_id}-${itemIndex}`} className="bg-gray-50 rounded-lg p-3">
                        {/* Nome prodotto con cestino a destra */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-gray-900 flex-1 break-words">{item.nome}</p>
                          {/* Cestino piccolo a destra del nome */}
                          <button
                            onClick={() => removeProdottoFromOrdine(item.uniqueId || item.id)}
                            className="flex items-center justify-center min-w-[32px] min-h-[32px] sm:min-w-[28px] sm:min-h-[28px] p-1.5 sm:p-1 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                            style={{ touchAction: 'manipulation' }}
                            aria-label="Rimuovi prodotto"
                          >
                            <Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                          </button>
                        </div>
                        
                        <div className="flex items-start justify-between gap-3">
                          {/* Tipologia, fornitore e note - con più spazio */}
                          <div className="flex-1 min-w-0">
                            {/* Selezione tipologia - mostra sempre dropdown se ci sono tipologie disponibili */}
                            {item.tipologie_disponibili && item.tipologie_disponibili.length > 0 ? (
                              <select
                                value={item.tipologia || item.tipologia_default_nome || (item.tipologie_disponibili && item.tipologie_disponibili.length > 0 ? item.tipologie_disponibili[0].nome : '')}
                                onChange={(e) => {
                                  const nuovaTipologia = e.target.value;
                                  console.log('🔍 Tipologia cambiata per', item.nome, ':', nuovaTipologia);
                                  const updated = ordineProdotti.map(p =>
                                    p.id === item.id ? { ...p, tipologia: nuovaTipologia } : p
                                  );
                                  setOrdineProdotti(updated);
                                }}
                                className="text-xs border border-blue-300 rounded px-2 py-1 w-full bg-white focus:ring-1 focus:ring-blue-500"
                              >
                                {item.tipologie_disponibili.map(tip => (
                                  <option key={tip.id || tip.nome} value={tip.nome}>
                                    {tip.nome}
                                  </option>
                                ))}
                              </select>
                            ) : item.tipologia && item.tipologia !== 'da_definire' ? (
                              <p className="text-xs text-gray-500">{item.tipologia}</p>
                            ) : (
                              <p className="text-xs text-gray-400 italic">Nessuna tipologia selezionata</p>
                            )}
                            
                            {/* Dropdown Fornitore */}
                            {fornitoriCategoria.length > 1 && (
                              <select
                                value={item.fornitore_id || ''}
                                onChange={(e) => updateFornitore(item.id, e.target.value)}
                                className="mt-1 text-xs border border-blue-300 rounded px-2 py-1 w-full bg-white focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="">Seleziona fornitore...</option>
                                {fornitoriCategoria.map(f => (
                                  <option key={f.id} value={f.id}>
                                    {f.nome}
                                  </option>
                                ))}
                              </select>
                            )}
                            
                            {fornitoriCategoria.length === 1 && (
                              <p className="mt-1 text-xs text-blue-600">
                                Fornitore: {fornitoriCategoria[0].nome}
                              </p>
                            )}
                            
                            <input
                              type="text"
                              placeholder="Note (opzionale)"
                              value={item.note || ''}
                              onChange={(e) => updateNote(item.id, e.target.value)}
                              className="mt-1 text-xs border border-gray-300 rounded px-2 py-1 w-full"
                            />
                          </div>
                          
                          {/* Controlli quantità - a destra */}
                          <div className="flex items-center flex-shrink-0">
                            <div className="flex items-center space-x-1 sm:space-x-2">
                              <button
                                onClick={() => updateQuantita(item.id, item.quantita - 1)}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                                style={{ touchAction: 'manipulation' }}
                                aria-label="Diminuisci quantità"
                              >
                                <Minus className="h-5 w-5 sm:h-4 sm:w-4" />
                              </button>
                              <span className="text-base sm:text-sm font-medium text-gray-900 min-w-[3rem] sm:min-w-[2rem] text-center">
                                {item.quantita}
                              </span>
                              <button
                                onClick={() => updateQuantita(item.id, item.quantita + 1)}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[36px] sm:min-h-[36px] p-2 sm:p-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg sm:rounded text-gray-700 hover:text-gray-900 transition-colors"
                                style={{ touchAction: 'manipulation' }}
                                aria-label="Aumenta quantità"
                              >
                                <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Campo Note Personalizzate per Fornitore */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📝 Note aggiuntive per {gruppo.fornitore.nome}
                    </label>
                    <textarea
                      value={notePersonalizzate[`fornitore_${fornitoreId}`] || ''}
                      onChange={(e) => handleNotaChange(`fornitore_${fornitoreId}`, e.target.value)}
                      placeholder="Es: Consegna entro le 10:00, prodotto fresco, confezione speciale..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows="2"
                    />
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={createOrdine}
                disabled={loading}
                className="flex-1 btn btn-primary"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <ShoppingCart className="h-4 w-4 mr-2" />
                )}
                {editingOrdine ? 'Aggiorna Ordine' : 'Salva Ordine'}
              </button>
              
              {user?.ruolo === 'admin' && (
                <button
                  onClick={sendWhatsAppMessages}
                  disabled={sendingWhatsApp}
                  className="flex-1 btn bg-green-600 hover:bg-green-700 text-white h-10 py-2 px-4"
                >
                  {sendingWhatsApp ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Salva e Invia WhatsApp
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {ordineProdotti.length === 0 && (
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun prodotto nell'ordine</h3>
          <p className="mt-1 text-sm text-gray-500">
            Inizia a cercare e aggiungere prodotti per creare il tuo ordine.
          </p>
        </div>
      )}

      {/* Modale di conferma invio WhatsApp */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Conferma Invio WhatsApp"
        message={`Sei sicuro di voler inviare ${Object.keys(groupByCategoria()).length} messaggi WhatsApp ai fornitori?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Invia WhatsApp"
        cancelText="Annulla"
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
      />

      {/* Modale ordine già esistente */}
      <Modal
        isOpen={showOrdineEsistenteModal}
        onClose={() => setShowOrdineEsistenteModal(false)}
        title="Ordine già Esistente"
        message={`Esiste già un ordine bozza per il ${new Date(dataOrdine).toLocaleDateString('it-IT')} che non è ancora stato inviato.\n\nNon puoi creare un nuovo ordine per questa data. Vai nella lista ordini e modifica quello esistente, oppure cambia la data di questo ordine.`}
        type="error"
        confirmText="Vai agli Ordini"
        cancelText="Chiudi"
        onConfirm={apriOrdineEsistente}
      />

      {/* Modale invio WhatsApp con pulsanti manuali */}
      {showWhatsAppLinksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900">
                📱 Invia Messaggi WhatsApp
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Clicca su ogni fornitore per inviare il messaggio. I messaggi si apriranno uno alla volta.
              </p>
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">
                  {whatsappLinks.filter(l => l.inviato).length} / {whatsappLinks.length} inviati
                </span>
              </div>
            </div>

            <div className="px-6 py-4 space-y-3">
              {whatsappLinks.map((linkData, index) => (
                <div 
                  key={index}
                  className={`border rounded-lg p-4 transition-all ${
                    linkData.inviato 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-medium text-gray-900">
                          {linkData.fornitore}
                        </span>
                        {linkData.inviato && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Inviato
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                        {linkData.messaggio.substring(0, 100)}...
                      </p>
                    </div>
                    <button
                      onClick={() => handleInviaWhatsAppSingolo(linkData)}
                      disabled={linkData.inviato}
                      className={`ml-4 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        linkData.inviato
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {linkData.inviato ? '✓ Inviato' : '📱 Invia'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              {whatsappLinks.filter(l => !l.inviato).length > 0 && (
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Attenzione: {whatsappLinks.filter(l => !l.inviato).length} messaggi non ancora inviati
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                {whatsappLinks.some(l => l.inviato) && (
                  <button
                    onClick={rigeneraMessaggiWhatsApp}
                    className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md text-sm font-medium"
                  >
                    🔄 Re-invia (rigenera tutti)
                  </button>
                )}
                <button
                  onClick={handleChiudiModalWhatsApp}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                >
                  {whatsappLinks.every(l => l.inviato) ? 'Fatto, Torna agli Ordini' : 'Chiudi (puoi completare dopo)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale di conferma eliminazione prodotto */}
      {/* Modal Selezione Tipologia */}
      {showTipologiaModal && prodottoPendingTipologia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Seleziona Tipologia
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Scegli la tipologia per <strong>{prodottoPendingTipologia.nome}</strong>
              </p>
            </div>
            <div className="px-6 py-4 space-y-2">
              {(prodottoPendingTipologia.tipologie_disponibili || []).map((tip) => {
                const isDefault = prodottoPendingTipologia.tipologia_default_nome === tip.nome;
                return (
                  <button
                    key={tip.id || tip.nome}
                    onClick={() => handleSelezioneTipologia(tip.nome)}
                    className={`w-full text-left px-4 py-3 border rounded-lg transition-colors ${
                      isDefault
                        ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                        : 'border-gray-300 hover:bg-blue-50 hover:border-blue-500'
                    }`}
                  >
                    <span className="font-medium text-gray-900">
                      {tip.nome}
                      {isDefault && <span className="ml-2 text-xs text-blue-600">(Default)</span>}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowTipologiaModal(false);
                  setProdottoPendingTipologia(null);
                }}
                className="btn btn-outline"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showDeleteProdottoModal}
        onClose={() => {
          setShowDeleteProdottoModal(false);
          setProdottoToDelete(null);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler rimuovere "${prodottoToDelete?.nome}" dall'ordine?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Sì, Rimuovi"
        cancelText="Annulla"
        onConfirm={executeRemoveProdotto}
      />
    </div>
  );
}

export default NuovoOrdine;

